"use client";

import { useState } from "react";
import { LoaderCircle, Sparkles } from "lucide-react";

import { t } from "@/lib/localization";
import { OpenEndedBlock as OpenEndedBlockType } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useOpenEndedFeedback } from "../../../api/useOpenEndedFeedback";
import RichText from "../text/richText";
import { PreviewButton } from "../shared/PreviewButton";
import RichHtml from "./richHtml";
import { seedTitleHtml, stripHtml } from "../../../lib/titleHtml";
import { fieldTag } from "../../../lib/sectionRegistry";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";

export default function OpenEndedBlock({
    block,
}: {
    block: OpenEndedBlockType;
}) {
    const locale = useLomdaLocale();
    const questionTag = fieldTag("open-ended", "question");
    const editable = useBlockEditable();
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const language = useLomdaV3Editor((s) => s.metadata.language);
    const { question, guidance } = block.data;

    const [answer, setAnswer] = useState("");
    const [feedback, setFeedback] = useState("");
    const { mutate, isPending } = useOpenEndedFeedback();

    const update = (p: Partial<OpenEndedBlockType["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...p });

    if (editable) {
        return (
            <div className="lomda-v3-card-surface flex flex-col gap-4 rounded-[20px] border border-[hsl(var(--lomda-border))] p-6">
                <div className="flex flex-col gap-1">
                    <RichText
                        initialHtml={seedTitleHtml(question, questionTag)}
                        defaultTag={questionTag}
                        editable
                        useGlobalToolbar
                        blockId={block.id}
                        placeholder={t(
                            "lomda-v3-editor.open-ended.question-placeholder"
                        )}
                        onCommit={(html) => update({ question: html })}
                    />
                    <span className="lomda-v3-chrome text-sm text-[hsl(var(--lomda-muted-text))]">
                        {t("lomda-v3-editor.open-ended.title-desc")}
                    </span>
                </div>
                <div className="flex flex-col gap-1.5">
                    <span className="lomda-v3-chrome flex items-center gap-1.5 text-sm font-semibold text-foreground">
                        <Sparkles className="size-4 text-[hsl(var(--lomda-primary))]" />
                        {t("lomda-v3-editor.open-ended.guidance-label")}
                    </span>
                    <textarea
                        value={guidance}
                        onChange={(e) => update({ guidance: e.target.value })}
                        rows={3}
                        placeholder={t(
                            "lomda-v3-editor.open-ended.guidance-placeholder"
                        )}
                        className="lomda-v3-chrome lomda-v3-page-text w-full resize-none rounded-xl border border-[hsl(var(--lomda-border))] bg-[hsl(var(--lomda-background))] p-3 text-base outline-none placeholder:text-[hsl(var(--lomda-muted-text))] focus:border-[hsl(var(--lomda-border-active))]"
                    />
                    <span className="lomda-v3-chrome text-sm text-[hsl(var(--lomda-muted-text))]">
                        {t("lomda-v3-editor.open-ended.guidance-desc")}
                    </span>
                </div>
            </div>
        );
    }

    if (!stripHtml(question)) return null;

    const submit = () => {
        const trimmed = answer.trim();
        if (!trimmed) return;
        mutate(
            { question: stripHtml(question), answer: trimmed, guidance, language },
            {
                onSuccess: (data) => {
                    if ("feedback" in data) setFeedback(data.feedback);
                },
            }
        );
    };

    return (
        <div className="flex flex-col gap-4">
            <RichHtml html={seedTitleHtml(question, questionTag)} />
            <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                disabled={isPending}
                rows={4}
                placeholder={t(
                    "lomda-v3-editor.open-ended.answer-placeholder",
                    locale
                )}
                className="lomda-v3-card-surface w-full resize-none rounded-[20px] border border-[hsl(var(--lomda-border))] p-4 lomda-v3-text-p outline-none placeholder:text-[hsl(var(--lomda-muted-text))] focus:border-[hsl(var(--lomda-primary))] disabled:opacity-60"
            />

            {feedback ? (
                <div className="flex flex-col gap-2 rounded-2xl bg-[hsl(var(--lomda-primary))]/8 p-4 animate-in fade-in">
                    <span className="flex items-center gap-1.5 lomda-v3-text-label text-[hsl(var(--lomda-primary))]">
                        <Sparkles className="size-4" />
                        {t("lomda-v3-editor.open-ended.feedback-title", locale)}
                    </span>
                    <p className="lomda-v3-text-p text-foreground">{feedback}</p>
                </div>
            ) : (
                <PreviewButton
                    type="button"
                    disabled={!answer.trim() || isPending}
                    onClick={submit}
                >
                    {isPending && (
                        <LoaderCircle className="size-4 animate-spin" />
                    )}
                    {isPending
                        ? t("lomda-v3-editor.open-ended.thinking", locale)
                        : t("lomda-v3-editor.open-ended.submit", locale)}
                </PreviewButton>
            )}
        </div>
    );
}
